import { useCallback } from 'react';
import { FieldArray } from 'react-final-form-arrays';
import cn from 'classnames';
import { Field } from 'react-final-form';

import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { Button } from 'UIPackage/MainModule/components/Button';
import { Row } from 'UIPackage/FormModule/components/Row';
import {
  IKnmErknmFormViolation,
  IViolationFixPlanExplanationItem,
} from 'KnmErknmPackage/ErknmViolationsModule/interfaces/IKnmErknmFormViolation';
import { ViolationStatusesEnum } from 'APIPackage/KnmModule/enums/ViolationStatusesEnum';
import { CloseButton } from 'UIPackage/MainModule/components/CloseButton';
import { getTitleFieldWithRedStar } from 'functions/getTitleFieldWithRedStar';
import { IDecisionFormRef } from 'DecisionPackage/MainModule/interfaces/IDecisionFormRef';

import styles from './ErknmDecisionViolationFixPlanMethodsField.module.scss';
import { ReactQuillEditorField } from '../../../UIPackage/MainModule/components/ReactQuillEditorField';
import { makeRequiredQuillEditorDelta } from '../../../CorePackage/MainModule/validators/isRequiredQuillEditorDelta';

interface IErknmDecisionViolationFixPlanExplanationsFieldProps {
  fieldName: string;
  formRef: IKnmFormRef | IDecisionFormRef | undefined;
  violation: IKnmErknmFormViolation;
  watchOnly: boolean;
  isPaperless: boolean;
  mId: string;
  isKnm?: boolean;
  isPm?: boolean;
  isDecision?: boolean;
  isResultDecisionSinged?: boolean;
  extractVersion?: number;
}

export const ErknmDecisionViolationFixPlanExplanationsField = ({
  fieldName,
  formRef,
  violation,
  watchOnly,
  isPaperless,
  mId,
  isKnm,
  isPm,
  isDecision,
  isResultDecisionSinged,
  extractVersion,
}: IErknmDecisionViolationFixPlanExplanationsFieldProps) => {
  const explanationsFieldName = `${fieldName}.fixPlanExplanations`;
  const fixPlanExplanationsRequiredFieldName = `${fieldName}.fixPlanExplanationsRequired`;
  const push = formRef?.current.mutators.push;
  const remove = formRef?.current.mutators.remove;
  const isOnApproval: boolean = violation.status === ViolationStatusesEnum.APPROVING;

  const isNotFixedByStatus: boolean = violation.status === ViolationStatusesEnum.NOT_FIXED;

  const handleAdd = useCallback(() => {
    const newExplanationItem: IViolationFixPlanExplanationItem = {
      isNew: true,
      value: '',
      guid: null,
      dateCreate: null,
      valueReactQuillEditor: null,
    };

    push && push(explanationsFieldName, newExplanationItem);
  }, [explanationsFieldName, push]);

  const handleRemove = useCallback(
    (idx: number) => () => {
      remove && remove(explanationsFieldName, idx);
    },
    [explanationsFieldName, remove],
  );

  return (
    <FieldBlock
      id={`${mId}.fixPlanExplanationsRequired`}
      label={getTitleFieldWithRedStar({
        label: 'Пояснение инспектора к устранению',
        field: fixPlanExplanationsRequiredFieldName,
        isDecision,
        isKnm,
        isPm,
      })}
      color={isPaperless ? undefined : 'black'}
      labelFontSize={isPaperless ? undefined : 'small'}
      className={cn({
        [styles.GroupMarginTop]: isPaperless,
        [styles.GroupMarginBottom]: isPaperless,
      })}
      button={
        (isNotFixedByStatus || !violation.status) && !isOnApproval && !watchOnly && !violation.fixes?.length ? (
          <Button
            className="shared-btn-add"
            text="Добавить"
            type="button"
            variant="link"
            color="primary"
            onClick={handleAdd}
          />
        ) : undefined
      }
    >
      <FieldArray name={explanationsFieldName}>
        {({ fields, meta }) => {
          return (
            <>
              {fields.map((explanationFieldName, idx) => {
                const explanationValue = fields.value[idx];

                const isSecondSign = typeof extractVersion === 'number' && extractVersion > 0;

                const isDisabled =
                  isResultDecisionSinged !== undefined
                    ? (isResultDecisionSinged || isSecondSign) && !explanationValue.isNew
                    : !explanationValue.isNew;

                return (
                  <Row key={explanationFieldName} id={`${mId}.fixPlanExplanations[${idx}].valueReactQuillEditor`}>
                    <ReactQuillEditorField
                      className={`shared-row-flex ${styles.FixPlanExplanationsTextarea}`}
                      key={explanationFieldName}
                      name={`${explanationFieldName}.valueReactQuillEditor`}
                      placeholder="Введите пояснение"
                      validate={makeRequiredQuillEditorDelta('Поле не может быть пустым')}
                      disabled={isDisabled}
                      handleCustomChange={(e: string) => {
                        // @ts-ignore
                        formRef?.current?.change(`${explanationFieldName}.value` as never, e);
                      }}
                    />
                    {!isDisabled && <CloseButton className="shared-deleting-row-btn" onClick={handleRemove(idx)} />}
                  </Row>
                );
              })}
              {typeof meta.error === 'string' && (
                <p className="shared-input-error shared-row__small-margin">{meta.error}</p>
              )}
              <Field name={fixPlanExplanationsRequiredFieldName}>
                {field => {
                  if (typeof field.meta.error === 'string' && field.meta.touched) {
                    return <p className="shared-input-error shared-row__small-margin">{field.meta.error}</p>;
                  }
                }}
              </Field>
            </>
          );
        }}
      </FieldArray>
    </FieldBlock>
  );
};
